import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchAccountTermNotifications } from "services/accountTermNotificationsApi";
import { formatDisplayDateTime } from "utils/dateFormat";

const displayMessage = (item) => {
  const raw = item?.metadata?.message;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return item?.content || "";
};

const directionLabel = (item) => {
  if (item?.direction === "SENT") {
    return `You → ${item?.recipientName || "recipient"}`;
  }
  return `${item?.senderName || "Staff member"} → You`;
};

const TermAccountNotificationsModal = ({
  open,
  onClose,
  clientAccountDetailId,
  termNumber,
  termLabel,
  clientId,
}) => {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["accountTermNotifications", clientAccountDetailId, termNumber, clientId],
    queryFn: () =>
      fetchAccountTermNotifications({
        clientAccountDetailId,
        termNumber,
        clientId,
      }),
    enabled: open && Boolean(clientAccountDetailId) && Boolean(termNumber),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Notifications{termLabel ? ` — ${termLabel}` : ""}</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Typography variant="body2" className="text-app-text-muted">
            Loading...
          </Typography>
        ) : items.length === 0 ? (
          <Typography variant="body2" className="text-app-text-muted">
            No notifications sent or received for this term yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {items.map((item, index) => (
              <div key={item.id ?? index}>
                {index > 0 ? <Divider component="li" /> : null}
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <span className="flex items-start justify-between gap-2">
                        <span className="flex items-center gap-1.5 flex-wrap">
                          <Chip
                            label={item?.direction === "SENT" ? "Sent" : "Received"}
                            size="small"
                            variant={item?.direction === "SENT" ? "outlined" : "filled"}
                            color={item?.direction === "SENT" ? "default" : "info"}
                            sx={{ height: 18, fontSize: 10 }}
                          />
                          <span className="whitespace-pre-wrap break-words">
                            {displayMessage(item)}
                          </span>
                        </span>
                        {item?.direction !== "SENT" && !item?.read ? (
                          <Chip label="Unread" size="small" color="primary" variant="outlined" />
                        ) : null}
                      </span>
                    }
                    secondary={
                      <>
                        {directionLabel(item)}
                        {item?.date ? ` · ${formatDisplayDateTime(item.date)}` : ""}
                      </>
                    }
                    secondaryTypographyProps={{ className: "text-app-text-muted" }}
                  />
                </ListItem>
              </div>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TermAccountNotificationsModal;
